<template>
  <div class="tab-content">
    <section class="settings-card">
      <div class="card-header">
        <div class="card-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 2a10 10 0 0 1 0 20"/><path d="M12 2c2.5 2.5 4 6 4 10s-1.5 7.5-4 10"/><line x1="2" y1="12" x2="22" y2="12"/></svg>
        </div>
        <div>
          <h3 class="card-title">Cores dos Relatórios</h3>
          <p class="card-desc">Personalize a cor de destaque usada nos PDFs de relatórios de obra e de manutenção.</p>
        </div>
      </div>

      <div class="color-section">
        <div class="color-row">
          <div class="color-info">
            <span class="color-label">Cor de destaque dos relatórios</span>
            <span class="color-desc">Esta cor é utilizada na faixa de cabeçalho e nos títulos de seção dos PDFs exportados, tanto de obra quanto de manutenção.</span>
          </div>
          <div class="color-picker-group">
            <input type="color" class="color-native" :value="reportColor" @input="onColorInput" />
            <div class="hex-wrap">
              <span class="hex-prefix">#</span>
              <input type="text" class="hex-input" :value="hexDisplay" maxlength="6" placeholder="0f4c81"
                @input="onHexInput" @blur="onHexBlur" />
            </div>
          </div>
        </div>

        <div class="preview-band" :style="{ background: reportColor }">
          <div class="preview-band-inner">
            <span class="preview-band-title">Nome da Obra · Relatório de Progresso</span>
            <span class="preview-band-meta">Seção de exemplo · Janeiro 2025</span>
          </div>
        </div>

        <div class="reset-row">
          <button type="button" class="btn-reset" @click="resetToInterface">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/></svg>
            Usar cor da interface ({{ interfaceColorHex }})
          </button>
          <button type="button" class="btn-reset btn-reset--secondary" @click="resetToDefault">
            Restaurar padrão (#0f4c81)
          </button>
        </div>
      </div>

      <div class="save-row">
        <button class="btn-save" type="button" @click="$emit('save')">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
          Salvar
        </button>
        <Transition name="fade"><span v-if="successText" class="save-success">{{ successText }}</span></Transition>
        <p v-if="errorText" class="save-error">{{ errorText }}</p>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch, type PropType } from 'vue'
import type { SystemConfig } from '@/store/system'

const DEFAULT_COLOR = '#0f4c81'

function normalizeHex(raw: string): string {
  const clean = raw.replace(/^#/, '').replace(/[^0-9a-fA-F]/g, '')
  if (clean.length === 3) return '#' + clean.split('').map(c => c + c).join('')
  if (clean.length === 6) return '#' + clean
  return DEFAULT_COLOR
}

export default defineComponent({
  name: 'SystemTabCoresRelatorios',
  props: {
    form: { type: Object as PropType<SystemConfig>, required: true },
    errorText: { type: String, default: '' },
    successText: { type: String, default: '' },
  },
  emits: ['update-form', 'save'],
  setup(props, { emit }) {
    const reportColor = computed(() => props.form.colors?.report || DEFAULT_COLOR)
    const interfaceColorHex = computed(() => props.form.colors?.primary || DEFAULT_COLOR)
    const hexDisplay = computed(() => reportColor.value.replace(/^#/, ''))

    const localHex = ref(hexDisplay.value)
    watch(() => hexDisplay.value, v => { localHex.value = v })

    function setColor(hex: string) {
      const current = props.form.colors || {}
      emit('update-form', { field: 'colors', value: { ...current, report: hex } })
    }

    function onColorInput(e: Event) {
      setColor((e.target as HTMLInputElement).value)
    }

    function onHexInput(e: Event) {
      localHex.value = (e.target as HTMLInputElement).value
    }

    function onHexBlur() {
      const norm = normalizeHex(localHex.value)
      setColor(norm)
    }

    function resetToInterface() {
      setColor(interfaceColorHex.value)
    }

    function resetToDefault() {
      setColor(DEFAULT_COLOR)
    }

    return { reportColor, interfaceColorHex, hexDisplay, localHex, onColorInput, onHexInput, onHexBlur, resetToInterface, resetToDefault }
  }
})
</script>

<style scoped lang="scss">
.tab-content { display: flex; flex-direction: column; gap: 16px; }
.settings-card { border: 1px solid rgba(31,30,26,.1); border-radius: 14px; background: var(--card,#fff); padding: 22px 24px; display: flex; flex-direction: column; gap: 20px; }
.card-header { display: flex; align-items: flex-start; gap: 14px; padding-bottom: 16px; border-bottom: 1px solid rgba(31,30,26,.07); }
.card-icon { width: 36px; height: 36px; border-radius: 9px; flex-shrink: 0; background: rgba(31,30,26,.06); display: flex; align-items: center; justify-content: center; }
.card-title { margin: 0 0 2px; font-size: 15px; font-weight: 700; color: var(--ink,#2c2b27); }
.card-desc { margin: 0; font-size: 12px; color: var(--ink-muted,#888); }
.color-section { display: flex; flex-direction: column; gap: 16px; }
.color-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.color-info { display: flex; flex-direction: column; gap: 4px; max-width: 400px; }
.color-label { font-size: 13px; font-weight: 700; color: var(--ink); }
.color-desc { font-size: 12px; color: var(--ink-muted); line-height: 1.5; }
.color-picker-group { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.color-native { width: 48px; height: 40px; border: 1.5px solid rgba(31,30,26,.15); border-radius: 10px; padding: 3px; cursor: pointer; background: none; }
.hex-wrap { display: flex; align-items: center; border: 1.5px solid rgba(31,30,26,.15); border-radius: 8px; overflow: hidden; background: #fff; &:focus-within { border-color: var(--accent,#0f4c81); } }
.hex-prefix { padding: 0 6px 0 10px; font-size: 13px; color: var(--ink-muted); font-family: monospace; }
.hex-input { border: none; outline: none; font-size: 13px; font-family: monospace; color: var(--ink); background: transparent; width: 72px; padding: 8px 10px 8px 0; text-transform: uppercase; }
.preview-band { border-radius: 10px; overflow: hidden; padding: 0; }
.preview-band-inner { padding: 18px 24px; display: flex; flex-direction: column; gap: 3px; }
.preview-band-title { font-size: 16px; font-weight: 700; color: #fff; font-family: 'Inter', 'Trebuchet MS', sans-serif; }
.preview-band-meta { font-size: 11px; color: rgba(255,255,255,.75); font-family: 'Inter', 'Trebuchet MS', sans-serif; }
.reset-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.btn-reset { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 8px; border: 1.5px solid rgba(31,30,26,.15); background: rgba(31,30,26,.04); cursor: pointer; font-size: 12px; color: var(--ink-muted); font-weight: 600; &:hover { background: rgba(31,30,26,.08); } &--secondary { color: var(--ink-muted); } }
.save-row { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.btn-save { display: inline-flex; align-items: center; gap: 8px; padding: 11px 24px; border-radius: 10px; border: none; cursor: pointer; background: var(--system-navbar,#0f4c81); color: #fff; font-size: 14px; font-weight: 700; &:hover { opacity: .88; } }
.save-success { font-size: 13px; font-weight: 600; color: #15803d; }
.save-error { margin: 0; font-size: 13px; color: #dc2626; }
.fade-enter-active, .fade-leave-active { transition: opacity .3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
